<template>
  <app-collapse class="mt-1">
    <app-collapse-item
      :is-visible="false"
      class="collapsible-section-bg"
      title="Transactions"
    >
      <template #header>
        <span
          class="lead collapse-title"
        >
          <section-status-icon
            :has-data="hasTransactionsData"
            :has-errors="hasSectionErrors"
          />
          Transactions
        </span>
      </template>
      <validation-observer>
        <b-row v-if="parcel.transaction_data">
          <b-col
            cols="12"
            md="3"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Total amount invoiced"
            >
              <b-form-group>
                <label>
                  Total amount invoiced
                  <small class="text-muted">1406000000</small>
                  <feather-icon
                    v-b-tooltip.hover.top="'Total amount invoiced for the shipment'"
                    icon="InfoIcon"
                    size="14"
                    class="ml-50 text-muted"
                  />
                </label>
                <b-form-input
                  v-model="parcel.transaction_data.invoice_amount"
                  v-b-tooltip.hover.top="getIssueMessage('transaction_data.invoice_amount')"
                  :class="getIssueClass('transaction_data.invoice_amount')"
                  type="number"
                  step="0.01"
                  :disabled="disabled"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>

            <validation-provider
              v-slot="{ errors }"
              name="Invoice currency"
              rules="required"
            >
              <b-form-group>
                <label>
                  Invoice currency <span class="text-danger">*</span>
                  <small class="text-muted">1405000000</small>
                  <feather-icon
                    v-b-tooltip.hover.top="'Currency used for the invoice'"
                    icon="InfoIcon"
                    size="14"
                    class="ml-50 text-muted"
                  />
                </label>
                <v-select
                  v-model="parcel.transaction_data.invoice_currency"
                  :options="currencyOptions"
                  :disabled="disabled"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>
          </b-col>

          <b-col
            cols="12"
            md="3"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Gross mass"
            >
              <b-form-group>
                <label>
                  Gross mass
                  <small class="text-muted">1804000000</small>
                  <feather-icon
                    v-b-tooltip.hover.top="'Total gross mass of goods in kilograms'"
                    icon="InfoIcon"
                    size="14"
                    class="ml-50 text-muted"
                  />
                </label>
                <b-input-group append="kg">
                  <b-form-input
                    v-model="parcel.transaction_data.total_gross_mass"
                    v-b-tooltip.hover.top="getIssueMessage('transaction_data.total_gross_mass')"
                    :class="getIssueClass('transaction_data.total_gross_mass')"
                    type="number"
                    step="0.001"
                    :disabled="disabled"
                    :state="errors.length > 0 ? false : null"
                  />
                </b-input-group>
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>
          </b-col>

          <b-col
            cols="12"
            md="3"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Total net mass"
            >
              <b-form-group>
                <label>
                  Total net mass
                  <small class="text-muted">180300000B</small>
                  <feather-icon
                    v-b-tooltip.hover.top="'Total net mass of goods in kilograms'"
                    icon="InfoIcon"
                    size="14"
                    class="ml-50 text-muted"
                  />
                </label>
                <b-input-group append="kg">
                  <b-form-input
                    v-model="parcel.transaction_data.total_net_weight"
                    v-b-tooltip.hover.top="getIssueMessage('transaction_data.total_net_weight')"
                    :class="getIssueClass('transaction_data.total_net_weight')"
                    type="number"
                    step="0.001"
                    :disabled="disabled"
                    :state="errors.length > 0 ? false : null"
                  />
                </b-input-group>
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>
          </b-col>

          <b-col
            cols="12"
            md="3"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Nature of transaction"
            >
              <b-form-group>
                <label>
                  Nature of transaction
                  <small class="text-muted">9905000000</small>
                  <feather-icon
                    v-b-tooltip.hover.top="'Nature of the transaction'"
                    icon="InfoIcon"
                    size="14"
                    class="ml-50 text-muted"
                  />
                </label>
                <v-select
                  v-model="parcel.transaction_data.transaction_nature"
                  :options="transactionNatureOptions"
                  :reduce="option => option.value"
                  :disabled="disabled"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>
          </b-col>
        </b-row>
      </validation-observer>
    </app-collapse-item>
  </app-collapse>
</template>

<script>
import {
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BInputGroup,
  VBTooltip,
} from 'bootstrap-vue'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import vSelect from 'vue-select'
import AppCollapse from '@core/components/app-collapse/AppCollapse.vue'
import AppCollapseItem from '@core/components/app-collapse/AppCollapseItem.vue'
import errorHighlightingMixin from '@/mixins/errorHighlightingMixin'
import SectionStatusIcon from '@/views/shipments/declarations/modals/components/h1/SectionStatusIcon.vue'

export default {
  components: {
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BInputGroup,
    ValidationObserver,
    ValidationProvider,
    vSelect,
    AppCollapse,
    AppCollapseItem,
    SectionStatusIcon,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  mixins: [errorHighlightingMixin],
  props: {
    parcel: {
      type: Object,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      currencyOptions: [
        'EUR', 'USD', 'GBP', 'JPY', 'CHF', 'CAD', 'AUD', 'CNY', 'SEK', 'NOK', 'DKK', 'PLN', 'CZK', 'HUF', 'RON', 'BGN', 'HRK', 'RUB', 'TRY', 'INR', 'BRL', 'ZAR', 'KRW', 'MXN', 'IDR', 'MYR', 'PHP', 'THB', 'SGD', 'NZD', 'HKD', 'AED', 'SAR', 'ILS', 'EGP',
      ],
      transactionNatureOptions: [
        { label: 'test', value: '1' },
      ],
    }
  },
  computed: {
    hasTransactionsData() {
      if (!this.parcel || !this.parcel.transaction_data) {
        return false
      }
      const {
        invoice_amount: invoiceAmount,
        invoice_currency: invoiceCurrency,
        total_gross_mass: totalGrossMass,
        total_net_weight: totalNetWeight,
        transaction_nature: transactionNature,
      } = this.parcel.transaction_data
      return !!(
        invoiceAmount
        || invoiceCurrency
        || totalGrossMass
        || totalNetWeight
        || transactionNature
      )
    },
    hasSectionErrors() {
      return Object.keys(this.issuesMap).some(key => key.startsWith('transaction_data.'))
    },
  },
}
</script>

<style scoped lang="scss">
@import '@core/scss/base/bootstrap-extended/variables';
@import '@core/scss/base/core/mixins/declarations-mixins';

.collapsible-section-bg {
  @include collapsible-section-bg;
}

.input-warning {
  @include input-warning;
}

.input-danger {
  @include input-danger;
}
</style>
